import { FileExcelOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Checkbox, DatePicker, message, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import WebGrid, { type WebGridColumn } from '../../../webgrid/src';
import { formatLeiWhole, type InventoryAtDateItem, toIsoDay } from '../../domain/controlling';
import { withCurrency } from '../../domain/controlRoom';
import { getPhysicalInventoryRowColor } from '../../domain/goodsSale';
import useFileDownload from '../../hooks/useFileDownload';
import useRequest from '../../hooks/useRequest';

const { Text } = Typography;

const DATE_FORMAT = 'YYYY-MM-DD';

interface InventoryAtDateResponse {
    success: boolean;
    data: { items: InventoryAtDateItem[] };
}

const renderFlag = (value: unknown) => <Checkbox checked={Boolean(value)} disabled />;

/**
 * "Inventar la data de" (legacy `tsInvLaData`): the goods still held in stock on
 * a chosen day, read-only, with an Excel listing of the same rows.
 */
const InventoryAtDatePanel = () => {
    const { t } = useTranslation();
    const request = useRequest();
    const downloadExcel = useFileDownload();
    const [date, setDate] = useState(() => toIsoDay(new Date()));
    const [items, setItems] = useState<InventoryAtDateItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);

    const loadItems = useCallback(
        async (day: string) => {
            setLoading(true);
            try {
                const result = await request<InventoryAtDateResponse>({
                    path: '/controlling/inventory-at-date',
                    params: { date: day },
                });
                setItems(result.data.items);
            } catch (error) {
                message.error(t('controlRoom.controlling.inventoryLoadError'));
                console.error('Failed to load inventory at date', error);
            } finally {
                setLoading(false);
            }
        },
        [request, t],
    );

    useEffect(() => {
        void loadItems(date);
    }, [loadItems, date]);

    const handleExport = async () => {
        setExporting(true);
        try {
            await downloadExcel(
                `/controlling/inventory-at-date/export?date=${encodeURIComponent(date)}`,
                `inventar_la_data_${date}.xlsx`,
            );
            message.success(t('common.exportSuccess'));
        } catch (error) {
            message.error(t('controlRoom.controlling.inventoryExportError'));
            console.error('Failed to export inventory at date', error);
        } finally {
            setExporting(false);
        }
    };

    const columns = useMemo<WebGridColumn<InventoryAtDateItem>[]>(
        () => [
            {
                key: 'status',
                title: t('controlRoom.controlling.status'),
                dataIndex: 'status',
                sortable: true,
                filterable: true,
            },
            {
                key: 'nrContract',
                title: t('controlRoom.controlling.contractNumber'),
                dataIndex: 'nrContract',
                sortable: true,
                filterable: true,
            },
            {
                key: 'denumire',
                title: t('controlRoom.controlling.description'),
                dataIndex: 'denumire',
                sortable: true,
                filterable: true,
            },
            { key: 'um', title: t('controlRoom.controlling.unit'), dataIndex: 'um' },
            {
                key: 'cantitate',
                title: t('controlRoom.controlling.quantity'),
                dataIndex: 'cantitate',
                align: 'right',
            },
            {
                key: 'sumainLei',
                title: withCurrency(t('controlRoom.controlling.amount'), 'LEI'),
                dataIndex: 'sumainLei',
                align: 'right',
                sortable: true,
                render: (value: unknown) => formatLeiWhole(value as number | null),
            },
            {
                key: 'inv',
                title: t('controlRoom.controlling.inStock'),
                dataIndex: 'inv',
                align: 'center',
                render: renderFlag,
            },
            {
                key: 'invFizic',
                title: t('controlRoom.controlling.physicallyFound'),
                dataIndex: 'invFizic',
                align: 'center',
                render: renderFlag,
            },
            {
                key: 'contabilitate',
                title: t('controlRoom.controlling.accounting'),
                dataIndex: 'contabilitate',
                align: 'center',
                render: renderFlag,
            },
            {
                key: 'mentiuni',
                title: t('controlRoom.controlling.notes'),
                dataIndex: 'mentiuni',
                filterable: true,
            },
            {
                key: 'expirationDate',
                title: t('controlRoom.controlling.expirationDate'),
                dataIndex: 'expirationDate',
                sortable: true,
                filterable: true,
            },
        ],
        [t],
    );

    return (
        <div data-testid="controlling-inventory-at-date">
            <Space style={{ marginBottom: 12 }} wrap>
                <Text>{t('controlRoom.controlling.inventoryDate')}</Text>
                <DatePicker
                    aria-label={t('controlRoom.controlling.inventoryDate')}
                    value={dayjs(date)}
                    format={DATE_FORMAT}
                    allowClear={false}
                    onChange={value => {
                        if (value) {
                            setDate(value.format(DATE_FORMAT));
                        }
                    }}
                />
                <Button icon={<ReloadOutlined />} onClick={() => loadItems(date)}>
                    {t('controlRoom.controlling.refresh')}
                </Button>
                <Button icon={<FileExcelOutlined />} loading={exporting} onClick={handleExport}>
                    {t('controlRoom.controlling.export')}
                </Button>
                <span data-testid="controlling-inventory-count">
                    {t('controlRoom.controlling.itemCount', { count: items.length })}
                </span>
            </Space>
            <WebGrid<InventoryAtDateItem>
                data={items}
                columns={columns}
                loading={loading}
                pagination
                pageSize={50}
                filterBar
                getRowStyle={item => ({ backgroundColor: getPhysicalInventoryRowColor(item) })}
                rowIdAccessor={item => item.id}
                height="calc(100vh - 320px)"
                emptyMessage={t('controlRoom.controlling.noInventory')}
            />
        </div>
    );
};

export default InventoryAtDatePanel;
